/* eslint-disable no-restricted-syntax */
import React, { useState } from 'react';

import { DEFAULT_PREVIEW_BRAND_COLOR, initialsOf, isDarkColor } from './types';

import { cn } from '@/lib/utils';

interface PreviewAvatarProps {
  /** Brand display name — used to derive initials for the fallback disc. */
  brandName: string;
  /** Brand color (hex) — fills the disc behind the image / initials. */
  brandColor?: string;
  /** Avatar image URL. When absent OR when it fails to load, initials show. */
  avatarUrl?: string;
  /**
   * Disc sizing + radius classes (e.g. `size-8 rounded-full`). The avatar
   * always clips its image with `overflow-hidden`, so callers only set size.
   */
  className?: string;
  /** Typography class for the initials fallback (e.g. `text-detail font-semibold`). */
  textClassName?: string;
}

/**
 * Canonical avatar disc for every platform-preview replica. Renders the brand
 * logo when one loads AND is a plausible avatar shape, and gracefully falls
 * back to brand-color + initials otherwise — no URL, a 404/DNS failure, a
 * slow-but-not-broken fetch, or a wordmark logo too wide/tall to read as an
 * avatar. Never the browser's broken `<img>` glyph, never a moment of
 * neither, and never initials showing through a transparent logo. Single
 * source so the fallback behaves identically on Instagram, X, Facebook,
 * LinkedIn, TikTok, YouTube, Pinterest, and Threads previews.
 *
 * 🔴 The initials layer is mounted underneath the image (`absolute inset-0`)
 * WHILE the image is loading, so there is no instant where the disc shows
 * neither a logo nor a letter — but it is UNMOUNTED the moment the image
 * reports `status === 'loaded'`, so it can never show through a logo with
 * transparent pixels (a dark, mostly-transparent wordmark on a same-colour
 * disc used to read as "IT" floating over logo fragments). A loaded image
 * whose aspect ratio falls outside 0.67–1.5 (not roughly square) is treated
 * as a failed avatar and falls back to initials instead of being cropped
 * into or floating inside a circular disc — this is what a wide wordmark
 * logo actually rendered as: an accidentally blank/mixed disc, not a broken
 * image.
 */
export const PreviewAvatar: React.FC<PreviewAvatarProps> = ({
  brandName,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  className,
  textClassName,
}) => {
  // Load status is keyed to the url it belongs to, and both live in ONE
  // state value so they can never desync. `trackedUrl !== avatarUrl` is
  // detected and reset DURING render (React's documented "adjust state while
  // rendering" pattern — calling `setState` mid-render is safe and re-renders
  // immediately before anything commits), not in a `useEffect`.
  //
  // A `useEffect(() => setStatus('loading'), [avatarUrl])` used to do this
  // reset, and it raced the `<img>`'s own load: the effect fires in a
  // SEPARATE, later pass than the render that already mounted the new
  // `<img src={avatarUrl}>`, and a cached image can fire `onLoad`
  // synchronously on mount. If that `onLoad` won the race (status →
  // 'loaded'), the effect still ran afterward and clobbered it back to
  // 'loading' — with nothing left to flip it forward again, since the image
  // had already fired its one `load` event. The avatar was then stuck
  // showing initials-over-opacity-0 forever. Resetting synchronously in the
  // SAME render that changes `avatarUrl` closes the gap: the reset always
  // happens before the new `<img>` node exists, so no browser load/error
  // event can possibly arrive before it.
  const [state, setState] = useState<{
    url: string | undefined;
    status: 'loading' | 'loaded' | 'errored';
  }>({ url: avatarUrl, status: 'loading' });
  if (state.url !== avatarUrl) {
    setState({ url: avatarUrl, status: 'loading' });
  }
  const status = state.url === avatarUrl ? state.status : 'loading';
  const setStatus = (next: 'loading' | 'loaded' | 'errored') =>
    setState({ url: avatarUrl, status: next });

  const initials = initialsOf(brandName);
  const useDarkText = !isDarkColor(brandColor);

  return (
    <div
      className={cn(
        'relative flex items-center justify-center shrink-0 overflow-hidden',
        className,
      )}
      style={{ backgroundColor: brandColor }}
      aria-hidden
    >
      {status !== 'loaded' ? (
        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center',
            useDarkText ? 'text-foreground' : 'text-white',
            textClassName,
          )}
        >
          {initials}
        </span>
      ) : null}
      {avatarUrl ? (
        <img
          key={avatarUrl}
          src={avatarUrl}
          alt=""
          className={cn(
            'absolute inset-0 size-full object-cover transition-opacity duration-150',
            status === 'loaded' ? 'opacity-100' : 'opacity-0',
          )}
          onLoad={(event) => {
            const image = event.currentTarget;
            const ratio =
              image.naturalWidth && image.naturalHeight
                ? image.naturalWidth / image.naturalHeight
                : 1;
            setStatus(ratio > 1.5 || ratio < 0.67 ? 'errored' : 'loaded');
          }}
          onError={() => setStatus('errored')}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
      ) : null}
    </div>
  );
};
